// Chemin: C:\gestion-copro\src\types\user.types.ts
// Types pour la gestion des utilisateurs - Copro Protect
// Session 8 : ajout status, gsm, etage, lots
// Session 37 : ajout coproprieteIds
// Session 40 : ajout rôles multi-copropriétés pour module Communications
// Session 60 : ajout admin_saas au type UserRole
// Session 88 : Gouvernance V2 — ajout rôle coordinateur, rétrocompat gestionnaire
// Session 100 : Chantier ESCALATOR — ajout rôle vigie (copropriétaire autonome)
// Session 140 : Enrichissement UserStatus (en_attente_paiement, expired) + statutAbonnement dans UserProfile
// Session 188 : Ajout rôle apporteur (partenaire commercial — Alain)
//               isApporteur() détecte apporteurInfo (double rôle vigie+apporteur possible)
// Session 217 : Ajout estBailleur dans VigieInfo — active module Suivi bailleur VIGIE
// Session 241 : Ajout estDemo dans UserProfile — compte démo commercial (chrono premier login)
// Session 247 : Ajout kitDemarrageVu dans UserProfile — flag fermeture bannière kit de démarrage

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// RÔLES ET STATUTS
// ============================================================================

/**
 * Rôles utilisateur
 * - admin_saas : Super administrateur SaaS (Dominique / DOM TECH & SERVICES)
 * - coordinateur : Coordinateur de copropriété (nouveau - Gouvernance V2)
 * - gestionnaire : Ancien nom du coordinateur (rétrocompat Firestore)
 * - cs : Membre du Conseil Syndical
 * - coproprietaire : Copropriétaire simple (rattaché à un CS)
 * - vigie : Copropriétaire autonome — formule VIGIE (Session 100 — Escalator)
 * - apporteur : Partenaire commercial dédié (compte sans autre rôle)
 * 
 * Session 188 : Un apporteur peut être AUSSI vigie (double casquette).
 * La détection se fait via apporteurInfo.identifiant, PAS via role seul.
 * Utiliser le helper isApporteur() pour vérifier.
 */
export type UserRole = 'admin_saas' | 'coordinateur' | 'gestionnaire' | 'cs' | 'coproprietaire' | 'vigie' | 'apporteur';

/**
 * Statut du compte utilisateur
 */
export type UserStatus = 'pending' | 'active' | 'rejected' | 'en_attente_paiement' | 'expired';

/**
 * Statut d'abonnement (VIGIE / CS payants)
 */
export type StatutAbonnement = 'en_attente_paiement' | 'actif' | 'expire';

/**
 * Fonction au sein du Conseil Syndical
 */
export type FonctionCS = 'president' | 'tresorier' | 'secretaire' | 'membre';

// ============================================================================
// LOTS (structure existante)
// ============================================================================

export type LotType = 'appartement' | 'cave' | 'parking' | 'autre';

export interface Lot {
  type: LotType;
  numero: string;
}

// ============================================================================
// RÔLES PAR COPROPRIÉTÉ (module Communications)
// ============================================================================

export interface RoleCopropriete {
  isAdmin: boolean;
  isMembreCS: boolean;
  fonctionCS?: FonctionCS;
  dateConfirmation?: Timestamp;
  confirmationIp?: string;
}

// ============================================================================
// INFORMATIONS VIGIE (Session 100 — Escalator)
// ============================================================================

export interface VigieInfo {
  nomCopropriete: string;
  adresse: string;
  nombreLots?: number;
  syndic?: string;
  // Session 217 : flag bailleur — active le module "Suivi bailleur" dans la sidebar
  // Activé via SENTINELLE (action update_profil_bailleur) ou manuellement en Firestore
  estBailleur?: boolean;
}

// ============================================================================
// INFORMATIONS APPORTEUR (Session 188)
// ============================================================================

/**
 * Informations spécifiques au rôle apporteur (partenaire commercial)
 * Stocké directement sur le UserProfile
 * 
 * IMPORTANT : un user peut avoir apporteurInfo SANS avoir role === 'apporteur'.
 * Cas typique : Alain est vigie (role: 'vigie') ET apporteur (apporteurInfo présent).
 * La détection se fait TOUJOURS via apporteurInfo.identifiant, jamais via role seul.
 */
export interface ApporteurInfo {
  identifiant: string;         // 'alain' — correspond à ResponsableProspect dans crm.types
  nomComplet: string;          // 'Alain LAZARSKI'
  telephone?: string;
  commission: number;          // 0.25 = 25%
  dateDebut: Timestamp;
}

// ============================================================================
// PROFIL UTILISATEUR
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  
  role: UserRole;
  status?: UserStatus;
  statutAbonnement?: StatutAbonnement;
  
  isGestionnaireSaas?: boolean;
  
  roles?: {
    [coproprieteId: string]: RoleCopropriete;
  };
  
  emailNotification?: string;
  
  gsm?: string;
  etage?: number;
  lots?: Lot[];
  
  coproprietaireId?: string;
  coproprieteIds?: string[];
  
  vigieInfo?: VigieInfo;
  
  // Session 188 : Présent si le user est AUSSI apporteur, quel que soit son role principal
  apporteurInfo?: ApporteurInfo;

  // Session 241 : Compte démo commercial — chrono démarre au premier login
  estDemo?: boolean;

  // Session 247 : Kit de démarrage — true quand l'utilisateur a cliqué "J'ai compris, je démarre"
  // Pose le flag via user.service.ts — supprime la bannière dashboard définitivement
  kitDemarrageVu?: boolean;
  
  preferences: {
    notifications: boolean;
    emailRecap: boolean;
    langue: 'fr';
  };
  
  lastLogin: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  
  validatedAt?: Timestamp;
  validatedBy?: string;
  rejectedAt?: Timestamp;
  rejectedBy?: string;
  rejectionReason?: string;
}

// Alias pour compatibilité
export type User = UserProfile;

// ============================================================================
// FORMULAIRES
// ============================================================================

export interface InscriptionFormData {
  nom: string;
  prenom: string;
  email: string;
  password: string;
  confirmPassword: string;
  gsm: string;
  etage: number;
  lots: Lot[];
}

export interface InscriptionVigieFormData {
  nom: string;
  prenom: string;
  email: string;
  password: string;
  confirmPassword: string;
  nomCopropriete: string;
  adresse: string;
  nombreLots?: number;
  syndic?: string;
}

export interface PendingUser {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  gsm: string;
  etage: number;
  lots: Lot[];
  createdAt: Timestamp;
}

// ============================================================================
// LABELS ET CONSTANTES
// ============================================================================

export const LOT_TYPE_LABELS: Record<LotType, string> = {
  appartement: 'Appartement',
  cave: 'Cave / Cavette',
  parking: 'Parking',
  autre: 'Autre'
};

export const ETAGE_LABELS: Record<number, string> = {
  0: 'RDC',
  1: '1er etage',
  2: '2eme etage'
};

export const FONCTION_CS_LABELS: Record<FonctionCS, string> = {
  president: 'Président',
  tresorier: 'Trésorier',
  secretaire: 'Secrétaire',
  membre: 'Membre'
};

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin_saas: 'Admin SaaS',
  coordinateur: 'Coordinateur',
  gestionnaire: 'Coordinateur',
  cs: 'Conseil Syndical',
  coproprietaire: 'Copropriétaire',
  vigie: 'VIGIE',
  apporteur: 'Apporteur'
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  pending: 'En attente de validation',
  active: 'Actif',
  rejected: 'Refusé',
  en_attente_paiement: 'En attente de paiement',
  expired: 'Expiré'
};

// ============================================================================
// HELPERS — VÉRIFICATION DES RÔLES
// ============================================================================

export function isAdminSaas(user: UserProfile): boolean {
  return user.role === 'admin_saas' || user.isGestionnaireSaas === true;
}

export function isGestionnaireSaas(user: UserProfile): boolean {
  return isAdminSaas(user);
}

export function isCoordinateurRole(user: UserProfile): boolean {
  return user.role === 'coordinateur' || user.role === 'gestionnaire';
}

export function isCoordinateurOuAdmin(user: UserProfile): boolean {
  return isCoordinateurRole(user) || isAdminSaas(user);
}

export function isAdminCopropriete(user: UserProfile, coproprieteId: string): boolean {
  if (isAdminSaas(user)) return true;
  return user.roles?.[coproprieteId]?.isAdmin === true;
}

export function isCoordinateurCopropriete(user: UserProfile, coproprieteId: string): boolean {
  return isAdminCopropriete(user, coproprieteId);
}

export function isMembreCS(user: UserProfile, coproprieteId: string): boolean {
  return user.roles?.[coproprieteId]?.isMembreCS === true;
}

export function peutVoter(user: UserProfile, coproprieteId: string): boolean {
  return isMembreCS(user, coproprieteId);
}

export function getFonctionCS(user: UserProfile, coproprieteId: string): FonctionCS | undefined {
  return user.roles?.[coproprieteId]?.fonctionCS;
}

export function isCoproprietaireSimple(user: UserProfile): boolean {
  return user.role === 'coproprietaire';
}

export function isCSRole(user: UserProfile): boolean {
  return user.role === 'cs';
}

export function hasAdminAccess(user: UserProfile): boolean {
  return isCoordinateurOuAdmin(user) || isCSRole(user);
}

// ============================================================================
// HELPERS VIGIE (Session 100 — Escalator)
// ============================================================================

export function isVigie(user: UserProfile): boolean {
  return user.role === 'vigie';
}

export function hasSentinelleAccess(user: UserProfile): boolean {
  return isCoordinateurOuAdmin(user) || isCSRole(user) || isVigie(user);
}

export function hasVigieAccess(user: UserProfile): boolean {
  return isVigie(user) || isAdminSaas(user);
}

// ============================================================================
// HELPERS APPORTEUR (Session 188)
// ============================================================================

/**
 * Vérifie si un utilisateur est un apporteur d'affaires (partenaire commercial)
 * 
 * Détection via apporteurInfo.identifiant, PAS via role seul.
 * Un VIGIE peut être AUSSI apporteur (double casquette — cas Alain).
 * Un user avec role === 'apporteur' (compte dédié) est aussi détecté.
 */
export function isApporteur(user: UserProfile): boolean {
  return user.role === 'apporteur' || !!user.apporteurInfo?.identifiant;
}

/**
 * Vérifie si un utilisateur a accès à l'espace apporteur (/apporteur)
 */
export function hasApporteurAccess(user: UserProfile): boolean {
  return isApporteur(user) || isAdminSaas(user);
}

/**
 * Récupère l'identifiant apporteur (ex: 'alain') pour filtrer les prospects CRM
 * Admin SaaS → null (voit tout)
 */
export function getApporteurIdentifiant(user: UserProfile): string | null {
  if (isAdminSaas(user)) return null;
  if (user.apporteurInfo?.identifiant) {
    return user.apporteurInfo.identifiant;
  }
  return null;
}

// ============================================================================
// HELPERS ABONNEMENT (Session 140)
// ============================================================================

export function isEnAttentePaiement(user: UserProfile): boolean {
  return user.status === 'en_attente_paiement' || user.statutAbonnement === 'en_attente_paiement';
}

export function isAbonnementExpire(user: UserProfile): boolean {
  return user.status === 'expired' || user.statutAbonnement === 'expire';
}

// ============================================================================
// HELPERS DÉMO (Session 241)
// ============================================================================

export function isCompteDemo(user: UserProfile): boolean {
  return user.estDemo === true;
}
